/**
 * Responsibility: Standardize AI-layer errors and user-facing diagnostics.
 * Scope: Shared error types, routing summaries, and message formatting only.
 */

export type AIErrorCode =
  | 'provider-not-registered'
  | 'provider-unavailable'
  | 'provider-disallowed'
  | 'no-provider'
  | 'provider-failed'
  | 'response-invalid-json'
  | 'response-invalid-shape'
  | 'auth-required'
  | 'subscription-required'
  | 'quota-exhausted'
  | 'mock-route'
  | 'config-missing'
  | 'unknown'

export type AIProviderAttemptStatus = 'missing' | 'unavailable' | 'disallowed' | 'failed' | 'ok'

export interface AIProviderAttempt {
  providerId: string
  providerLabel: string
  model: string
  available: boolean
  allowed: boolean
  status: AIProviderAttemptStatus
  error?: string
}

export interface AIErrorDetail {
  jobType?: string
  providerId?: string
  providerLabel?: string
  model?: string
  attempts?: AIProviderAttempt[]
  hint?: string
  actionLabel?: string
}

function humanizeProviderId(providerId: string): string {
  const id = String(providerId ?? '').trim()
  if (!id) return 'AI provider'
  switch (id) {
    case 'vertex-managed': return 'Managed Cloud AI'
    case 'gemini': return 'Google Gemini'
    case 'openai': return 'OpenAI'
    case 'anthropic': return 'Anthropic Claude'
    case 'groq': return 'Groq'
    case 'ollama': return 'Ollama (Local)'
    case 'mock': return 'Mock provider'
    default: return id
  }
}

function summarizeAttempts(attempts: AIProviderAttempt[] = []): string[] {
  return attempts.map((attempt) => {
    const state = attempt.status === 'ok'
      ? 'used'
      : attempt.status === 'missing'
        ? 'not registered'
        : attempt.status === 'unavailable'
          ? 'unavailable'
          : attempt.status === 'disallowed'
            ? 'blocked'
            : 'failed'
    const model = attempt.model ? ` / ${attempt.model}` : ''
    const error = attempt.error ? ` — ${attempt.error}` : ''
    return `${attempt.providerLabel}${model}: ${state}${error}`
  })
}

export class AIError extends Error {
  readonly code: AIErrorCode
  readonly detail: AIErrorDetail

  constructor(code: AIErrorCode, message: string, detail: AIErrorDetail = {}, cause?: unknown) {
    super(message)
    this.name = 'AIError'
    this.code = code
    this.detail = detail
    if (cause !== undefined) {
      ;(this as Error & { cause?: unknown }).cause = cause
    }
  }
}

export function isAIError(error: unknown): error is AIError {
  return error instanceof AIError
}

export function createAIError(code: AIErrorCode, detail: AIErrorDetail = {}, cause?: unknown): AIError {
  const providerLabel = detail.providerLabel || (detail.providerId ? humanizeProviderId(detail.providerId) : '')
  const providerPart = providerLabel ? ` (${providerLabel}${detail.model ? ` / ${detail.model}` : ''})` : ''
  const jobPart = detail.jobType ? ` for ${detail.jobType}` : ''
  let baseMessage = ''

  switch (code) {
    case 'provider-not-registered':
      baseMessage = `Requested AI provider${providerPart} is not registered${jobPart}.`
      break
    case 'provider-unavailable':
      baseMessage = `Requested AI provider${providerPart} is not reachable right now${jobPart}.`
      break
    case 'provider-disallowed':
      baseMessage = `Requested AI provider${providerPart} is blocked by the current access mode${jobPart}.`
      break
    case 'no-provider':
      baseMessage = `No usable AI provider was found${jobPart}.`
      break
    case 'provider-failed':
      baseMessage = `The AI provider${providerPart} failed while handling${jobPart}.`
      break
    case 'response-invalid-json':
      baseMessage = `The AI provider${providerPart} returned invalid JSON${jobPart}.`
      break
    case 'response-invalid-shape':
      baseMessage = `The AI provider${providerPart} returned an incomplete response${jobPart}.`
      break
    case 'auth-required':
      baseMessage = `The AI provider${providerPart} requires sign-in or an API key${jobPart}.`
      break
    case 'subscription-required':
      baseMessage = `The AI provider${providerPart} requires an active subscription${jobPart}.`
      break
    case 'quota-exhausted':
      baseMessage = `The managed AI quota is exhausted${jobPart}.`
      break
    case 'mock-route':
      baseMessage = `The forge routed to the mock provider${jobPart}, which is not allowed here.`
      break
    case 'config-missing':
      baseMessage = `AI configuration is incomplete${jobPart}.`
      break
    default:
      baseMessage = `The AI request could not complete${jobPart}.`
      break
  }

  const hint = detail.hint?.trim()
  const suffix = hint ? ` ${hint}` : ''
  return new AIError(code, `${baseMessage}${suffix}`.trim(), detail, cause)
}

export function formatAIErrorMessage(error: unknown): string {
  if (!error) return 'The AI request could not complete.'
  if (isAIError(error)) return error.message
  if (error instanceof Error) return error.message
  return String(error)
}

export function buildAIErrorHint(error: AIError): string {
  const detail = error.detail
  if (detail.hint) return detail.hint
  switch (error.code) {
    case 'provider-not-registered':
      return 'Enable the provider or choose a model from a provider that is actually configured.'
    case 'provider-unavailable':
      return 'Check the key, proxy URL, or local service health, then try again.'
    case 'provider-disallowed':
      return 'Switch to a BYOK or local provider, or change the current access mode.'
    case 'no-provider':
      return 'At least one enabled provider must be reachable for the selected job.'
    case 'response-invalid-json':
      return 'Pick a stronger model or tighten the prompt so the provider returns valid JSON.'
    case 'response-invalid-shape':
      return 'Pick a stronger model or review the prompt and required output fields.'
    case 'auth-required':
      return 'Sign in, add the API key, or restore the missing provider credentials.'
    case 'subscription-required':
      return 'Confirm the hosted provider subscription or switch to another provider.'
    case 'quota-exhausted':
      return 'Wait for quota reset or switch to your own keys or local Ollama.'
    case 'mock-route':
      return 'Choose a real provider/model in AI settings before forging.'
    case 'config-missing':
      return 'Open AI settings and fill in the missing provider details.'
    default:
      return 'Open AI settings and check the selected provider/model.'
  }
}

export function summarizeAIError(error: unknown): { title: string; copy: string; details: string[]; actionLabel: string } {
  if (isAIError(error)) {
    return {
      title: error.code === 'provider-disallowed' || error.code === 'mock-route'
        ? 'AI routing needs attention'
        : error.code === 'provider-unavailable' || error.code === 'no-provider'
          ? 'AI provider unavailable'
          : 'AI configuration needs attention',
      copy: error.message,
      details: [
        ...(error.detail.jobType ? [`Job: ${error.detail.jobType}`] : []),
        ...(error.detail.providerId ? [`Provider: ${humanizeProviderId(error.detail.providerId)}${error.detail.model ? ` / ${error.detail.model}` : ''}`] : []),
        ...(error.detail.attempts?.length ? summarizeAttempts(error.detail.attempts) : []),
        buildAIErrorHint(error),
      ],
      actionLabel: error.detail.actionLabel || 'Open AI settings',
    }
  }

  const copy = formatAIErrorMessage(error) || 'The AI request could not complete.'
  return {
    title: 'AI request failed',
    copy,
    details: [copy],
    actionLabel: 'Open AI settings',
  }
}
